/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { Validator } from 'redux-form';
import { inputFormatters, generateDateFromValue, areValidDayAndMilliseconds } from '@blend/shared-react';
import { MASKED_SSN } from '@shared/constants/kba';
import { AddressInput } from '@components/form/Inputs/AddressInput/types';
import { isNil, trim, includes, every, get } from '@utils/lodash';

const { MIN_DATE, MAX_DATE } = inputFormatters.DATE;

function isEmptyValue(value: string) {
  return isNil(value) || value === '';
}

export const required: Validator = value => {
  return !isEmptyValue(value) ? undefined : 'This field is required.';
};

/** Validate that field is not null/undefined/empty string/all space characters */
export const notBlankValidator =
  (message = 'This field is required.') =>
  (value: string | undefined | null) => {
    return isNil(value) || value.trim().length === 0 ? message : undefined;
  };

function isTextValid(value: string) {
  return trim(value).length > 0;
}

/**
 * Text fields are valid if they contain more than whitespace.
 */
export const textRequired: Validator = value => (isTextValid(value) ? undefined : 'This field is required.');

export const phone: Validator = value => {
  return (value || '').toString().length !== 10 ? 'Please enter a complete phone number.' : undefined;
};

export const ssn: Validator = value => {
  // we accept an all 0 value from frontend to indicate the value already exists in our database
  if (value === MASKED_SSN) {
    return undefined;
  }
  const ssnRegex =
    /^(?!666|000|9\d{2})\d{3}(?!00)\d{2}(?!0{4})\d{4}(?<!123456789|0{9}|1{9}|2{9}|3{9}|4{9}|5{9}|6{9}|7{9}|8{9}|9{9})$/;
  const trimmedValue = (value || '').trim();
  if (trimmedValue.length !== 9) {
    return 'Please enter a complete SSN.';
  }
  return ssnRegex.test(trimmedValue) ? undefined : 'Please enter a valid SSN.';
};

export const postalCodeValidator: Validator = value => {
  return value.length === 5 ? undefined : 'Postal code must be 5 digits.';
};

export const stateCodeValidator: Validator = value => {
  return value.length === 2 ? undefined : 'State code must be 2 characters.';
};

export const countryCodeValidator: Validator = value => {
  return [2, 3].includes(value.length) ? undefined : 'Country code must be between 2 and 3 characters.';
};

export const requiredCheckboxValidator: Validator = value => {
  return !!value ? undefined : `Confirm that you've read and agree to the terms above.`;
};

export const dateGenerator: Validator = value => {
  if (!value || includes(value, '_')) {
    return 'Please enter a complete date.';
  }

  const dateMetadata = generateDateFromValue(value, 'en_US');
  if (!dateMetadata) {
    return 'Please enter a valid date.';
  }

  const { dateValue, day, year: dateYear } = dateMetadata;
  if (!areValidDayAndMilliseconds(dateValue, day)) {
    return 'Please enter a valid date.';
  }

  const milliseconds = dateValue.getTime();
  if (dateYear < MIN_DATE.year || milliseconds > MAX_DATE.valueOf() || milliseconds < MIN_DATE.valueOf()) {
    const [minString, maxString] = [MIN_DATE, MAX_DATE].map(dateTime => dateTime.toFormat('MM/dd/yyyy'));
    return `Please enter a date between ${minString} and ${maxString}.`;
  }

  return undefined;
};

// http://stackoverflow.com/questions/46155/validate-email-address-in-javascript
export const email: Validator = (value: string) => {
  if (isEmptyValue(value)) {
    return 'Email is required';
  }
  const emailRegEx =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
  return emailRegEx.test(value) ? undefined : 'Please enter a valid email';
};

export const address = (value: AddressInput.Address) => {
  const isValid = every(['line1', 'city', 'stateCode', 'postalCode', 'countryCode'], field => !!get(value, field));
  return isValid ? undefined : 'Please enter a valid address.';
};

/**
 * Validate that signature of participant (in Edit participant modal) must have at least two words.
 * The reason for this check is that the signature is splitted into first name and last name before sending to
 * Document Service and Document Service validate that lastname is not an empty string.
 * */
export const signatureHasAtLeastTwoWordsValidator = (value?: string): string | undefined => {
  if (!value) {
    return 'Signature is required.';
  }
  const words = value.split(' ').filter(w => w !== '');
  if (words.length < 2) {
    return `This field must include the participant's first and last name`;
  } else {
    return;
  }
};

export const notaryFeeValidator: Validator = (value?: string): string | undefined => {
  if (!value) {
    return 'Please enter a fee.';
  }
  if (isNaN(parseFloat(value))) {
    return 'Please enter a valid fee.';
  }
  if (parseFloat(value) < 0) {
    return 'Please enter a positive fee.';
  }
  if (parseFloat(value) > 30) {
    return 'Please enter a fee less than $30.';
  }
  return;
};

export interface NetworkConnectionTestResults {
  areAllTestsCompleted: boolean;
  allTestsPassed: boolean;
  audioEnabled: boolean;
  videoEnabled: boolean;
}

export const networkTestsValidator: Validator = (validationResults?: NetworkConnectionTestResults) => {
  // validationResults are undefined if the network tests have not fully rendered in the UI
  if (validationResults === undefined || !validationResults.areAllTestsCompleted) {
    return "Almost there! We're running some tests to ensure a high quality Audio/Video experience.";
  }

  if (!validationResults.audioEnabled || !validationResults.videoEnabled) {
    return 'Please enable your device camera and microphone to continue. Instructions can be found by clicking the info link below.';
  }

  return validationResults.allTestsPassed
    ? undefined
    : `This device is not compatible for the signing appointment. Please try using another device for your RON session.`;
};
